import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import {
  GetAgentsResponse,
  GetSelectedDailyListItemsResponse,
  GetDailyListProgressResponse,
  UpdateDailyListProgressRequest,
  Agent,
  DailyListItem,
} from '@dreamai/types';
import DailyLogo from './DailyLogo';

import { ReactComponent as ArrowRightIcon } from '../../icons/arrow-right.svg';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import { ReactComponent as RightCaretIcon } from '../../icons/right-caret.svg';
import { ReactComponent as CheckCircleIcon } from '../../icons/check-circle.svg';
import fireIcon from '../../icons/dream-daily/fire.png';
import { ReactComponent as AlarmClockIcon } from '../../icons/alarm-clock.svg';
import { ReactComponent as PhoneIcon } from '../../icons/phone.svg';
import { ReactComponent as PhoneHangupIcon } from '../../icons/phone-hangup.svg';
import { ReactComponent as IgnoreCircleIcon } from '../../icons/ignore-circle.svg';
import { ReactComponent as DreamDailyCompleteIcon } from '../../icons/dream-daily-complete.svg';
import { ReactComponent as DreamDailyIncompleteIcon } from '../../icons/dream-daily-incomplete.svg';
import { ReactComponent as DreamLogoCompact } from '../../icons/dream-logo-compact.svg';
import { QRCodeSVG } from 'qrcode.react';
import SidewaysLoading from './sidebar/SidewaysLoading';
import VoiceRecorder from './VoiceRecorder';
import ConfirmationSpecialView, {
  hasSpecialView,
} from '../ConfirmationSpecialView';
import { UIUpdate } from './canvas/UIComponentRenderer';
import ProDocAttachment from './ProDocAttachment';
import GenericDailyCard, { SnoozeOption } from './GenericDailyCard';
import ReactMarkdown from 'react-markdown';
import WebRTCVoiceChat from '../WebRTCVoiceChat';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import './dream-daily.scss';

type ScreenState = 'splash' | 'splash2' | 'dailyList' | 'completion';

const DreamDaily: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { voiceCallingEnabled } = useFeatureFlags();
  const [searchParams, setSearchParams] = useSearchParams();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [dailyListItems, setDailyListItems] = useState<DailyListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [screenState, setScreenState] = useState<ScreenState>('splash');
  const [currentItemIndex, setCurrentItemIndex] = useState<number>(0);
  const [processingAction, setProcessingAction] = useState<
    'confirm' | 'decline' | 'ignore' | 'snooze' | null
  >(null);
  const [streak, setStreak] = useState<number>(0);
  const [showingPreDisplayImage, setShowingPreDisplayImage] =
    useState<boolean>(false);
  const [questionAnswer, setQuestionAnswer] = useState<string>('');
  const [showNonMobileWarning, setShowNonMobileWarning] =
    useState<boolean>(true);
  const [activeCallItemId, setActiveCallItemId] = useState<string | null>(null);
  const [shouldHangupCall, setShouldHangupCall] = useState(false);
  const [swipedItemId, setSwipedItemId] = useState<string | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const swipeStartX = React.useRef<number | null>(null);

  // Filter out completed, ignored, and snoozed items
  const visibleDailyListItems = useMemo(() => {
    const now = new Date();
    return dailyListItems.filter((item) => {
      // Hide completed/ignored items
      if (item.status === 'complete' || item.status === 'ignored') return false;
      // Hide snoozed items (snoozeUntil is in the future)
      if (!item.snoozeUntil) return true;
      return new Date(item.snoozeUntil) <= now;
    });
  }, [dailyListItems]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = await getAccessTokenSilently();

        // Fetch agents and daily list items in parallel
        const [agentsResponse, dailyListResponse] = await Promise.all([
          api.get<GetAgentsResponse>('/agents', {
            headers: { Authorization: `Bearer ${token}` },
          }),
          api.get<GetSelectedDailyListItemsResponse>('/daily-list/selected', {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setAgents(agentsResponse.data.agents);
        const items = dailyListResponse.data.items;

        // Sort items: completed first, then pending by orderInList
        const sortedItems = [...items].sort((a, b) => {
          // Completed items come first
          if (a.status === 'complete' && b.status !== 'complete') return -1;
          if (a.status !== 'complete' && b.status === 'complete') return 1;

          // Within same status, sort by orderInList
          return (a.orderInList || 0) - (b.orderInList || 0);
        });

        setDailyListItems(sortedItems);

        // After loading items, fetch progress (fail silently if it fails)
        try {
          const progressResponse = await api.get<GetDailyListProgressResponse>(
            '/daily-list/progress',
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          if (progressResponse.data.success) {
            // Store the streak
            setStreak(progressResponse.data.streak);

            // Check if user has completed all items
            if (progressResponse.data.isComplete) {
              setScreenState('completion');
            } else {
              const currentId = progressResponse.data.currentDailyListItemId;
              if (currentId) {
                const index = sortedItems.findIndex(
                  (item) => item.id === currentId,
                );
                if (index !== -1) {
                  setScreenState('dailyList');
                  setCurrentItemIndex(index);
                  // Check if this item has a pre-display image
                  if (sortedItems[index]?.preDisplayImageUrl) {
                    setShowingPreDisplayImage(true);
                  }
                }
              } else {
                // No progress, just set the index (stay on splash screen)
                const firstPendingIndex = sortedItems.findIndex(
                  (item) => item.status !== 'complete',
                );
                if (firstPendingIndex !== -1) {
                  setCurrentItemIndex(firstPendingIndex);
                  // Check if this item has a pre-display image
                  if (sortedItems[firstPendingIndex]?.preDisplayImageUrl) {
                    setShowingPreDisplayImage(true);
                  }
                }
              }
            }
          }
        } catch (progressError) {
          // No progress, skip completed items and start from first pending
          console.log('Could not fetch progress, starting from first pending');
          const firstPendingIndex = sortedItems.findIndex(
            (item) => item.status !== 'complete',
          );
          if (firstPendingIndex !== -1) {
            setCurrentItemIndex(firstPendingIndex);
            // Check if this item has a pre-display image
            if (sortedItems[firstPendingIndex]?.preDisplayImageUrl) {
              setShowingPreDisplayImage(true);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching dream daily data:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load daily data',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAccessTokenSilently]);

  // Function to update progress on the backend
  const updateProgress = async (itemId: string) => {
    try {
      const token = await getAccessTokenSilently();
      const requestBody: UpdateDailyListProgressRequest = {
        dailyListItemId: itemId,
      };

      await api.post('/daily-list/progress', requestBody, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log(`Progress updated to item: ${itemId}`);
    } catch (err) {
      // Fail silently - progress update is not critical
      console.error('Failed to update progress:', err);
    }
  };

  // Update progress when currentItemIndex changes (and we're in dailyList state)
  useEffect(() => {
    if (screenState === 'dailyList' && visibleDailyListItems.length > 0) {
      const currentItem = visibleDailyListItems[currentItemIndex];
      if (currentItem) {
        updateProgress(currentItem.id);
      }
    }
  }, [currentItemIndex, screenState, visibleDailyListItems]);

  const formatDate = () => {
    const date = new Date();
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });

    // Add ordinal suffix (st, nd, rd, th)
    const ordinal = (n: number) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    return `${dayName}, ${ordinal(day)} of ${month}`;
  };

  // Format date as "Thursday 8 November 2025"
  const formatDateLong = () => {
    const date = new Date();
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    return `${dayName} ${day} ${month} ${year}`;
  };

  // Derive current view from query params
  const currentView = useMemo(() => {
    const view = searchParams.get('view');
    const itemId = searchParams.get('itemId');
    if (view === 'updates') return 'updates' as const;
    if (view === 'item' && itemId) return 'item' as const;
    return 'list' as const;
  }, [searchParams]);

  const selectedItemId = searchParams.get('itemId');
  const selectedItem = visibleDailyListItems.find(
    (item) => item.id === selectedItemId,
  );

  // Navigation helpers
  const navigateToList = () => {
    setSearchParams({});
  };

  const navigateToItem = (itemId: string) => {
    setSearchParams({ view: 'item', itemId });
  };

  const navigateToUpdates = () => {
    setSearchParams({ view: 'updates' });
  };

  if (loading) {
    return (
      <div className='dream-daily-container'>
        <div className='dream-daily-loading'>Loading your daily list...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='dream-daily-container'>
        <div className='dream-daily-error'>
          <h2>Error Loading Daily List</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Check if user is on mobile
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Non-mobile warning overlay (only show on desktop)
  if (showNonMobileWarning && !isMobile) {
    return (
      <div className='dream-daily-container no-v-center'>
        <div className='dream-daily-splash'>
          <div className='non-mobile-warning'>
            <DreamLogoCompact />

            <div className='headings-wrapper'>
              <h1>Daily is easier on your phone</h1>
              <p>Scan the QR Code below to get started</p>
              <button onClick={() => setShowNonMobileWarning(false)}>
                Show my Dream Daily here <ArrowRightIcon />
              </button>
            </div>

            <div className='qr-code-wrapper'>
              <div className='daily-logo-wrapper'>
                <DailyLogo />
              </div>
              <div className='qr-code-true-wrapper'>
                <QRCodeSVG
                  value={window.location.href}
                  size={160}
                  bgColor='#ffd601'
                />
              </div>
              <p>Crush your to-do list, grow your business</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Splash screen
  if (screenState === 'splash') {
    return (
      <div className='dream-daily-container'>
        <div className='dream-daily-splash'>
          <div className='sexy-middle'>
            <div className='splash-one-center'>
              <div className='daily-splash'>
                <div className='top-logo-tellers'>
                  <DreamLogoCompact />
                  <DailyLogo />
                </div>
                <div className='fancy-glow'></div>
              </div>
              <div className='pep-talk'>
                Crush your to-do list,
                <br />
                grow your business.
              </div>
            </div>
          </div>
          <div className='fire-area'>
            <button onClick={() => setScreenState('splash2')}>
              Next <ArrowRightIcon />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Splash 2 screen with agent images
  if (screenState === 'splash2') {
    return (
      <div className='dream-daily-container'>
        <div className='dream-daily-splash'>
          <div className='sexy-middle'>
            <div className='splash-two-center'>
              <div className='daily-splash'>
                <DailyLogo />
                <div className='fancy-glow'></div>
              </div>
              <div
                className={`agent-images agent-count-${agents.length >= 3 ? 'many' : agents.length}`}
              >
                {agents.map((agent, index) => (
                  <img
                    key={agent.id}
                    src={agent.image_url || ''}
                    alt={agent.name}
                    className={`agent-image agent-position-${index}`}
                    style={{ zIndex: agents.length - index }}
                  />
                ))}
              </div>
              <div className='pep-talk'>
                <p>Your team’s been working hard</p>
                <p>
                  Take 5 minutes to give them feedback and help grow your
                  business.
                </p>
              </div>
            </div>
          </div>
          <div className='fire-area'>
            <button onClick={() => setScreenState('dailyList')}>
              Start now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Advance to next item
  const advanceToNextItem = () => {
    if (currentItemIndex < visibleDailyListItems.length - 1) {
      const nextIndex = currentItemIndex + 1;
      const nextItem = visibleDailyListItems[nextIndex];

      // Check if the next item has a pre-display image
      if (nextItem?.preDisplayImageUrl) {
        setShowingPreDisplayImage(true);
      } else {
        setShowingPreDisplayImage(false);
      }

      setCurrentItemIndex(nextIndex);
    } else {
      // User has completed all items, show completion screen
      setScreenState('completion');
    }
  };

  // Handle confirm action
  const handleConfirmAction = async (itemId: string) => {
    setProcessingAction('confirm');

    // Call API in background
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/daily-list/confirm',
        { dailyListItemId: itemId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
    } catch (error) {
      console.error('Error confirming action:', error);
      // Silently fail - item already advanced
    } finally {
      // Clear processing state after 1 second
      setTimeout(() => {
        setProcessingAction(null);
      }, 1000);
    }
  };

  // Handle decline action
  const handleDeclineAction = async (itemId: string) => {
    // Optimistically advance and show spinner
    setProcessingAction('decline');
    advanceToNextItem();

    // Call API in background
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/daily-list/decline',
        { dailyListItemId: itemId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
    } catch (error) {
      console.error('Error declining action:', error);
      // Silently fail - item already advanced
    } finally {
      // Clear processing state after 1 second
      setTimeout(() => {
        setProcessingAction(null);
      }, 1000);
    }
  };

  // Handle ignore action
  const handleIgnoreAction = async (itemId: string) => {
    setProcessingAction('ignore');

    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/daily-list/ignore',
        { dailyListItemId: itemId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      // Navigate back to daily list after ignoring
      advanceToNextItem();
    } catch (error) {
      console.error('Error ignoring action:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  // Handle snooze action
  const handleSnoozeAction = async (
    itemId: string,
    snoozeOption: SnoozeOption,
  ) => {
    setProcessingAction('snooze');

    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/daily-list/snooze',
        { dailyListItemId: itemId, snoozeOption },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      // Navigate back to daily list after snoozing
      advanceToNextItem();
    } catch (error) {
      console.error('Error snoozing action:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  // Handle proceeding from pre-display image to actual item
  const handleProceedFromPreDisplay = () => {
    setShowingPreDisplayImage(false);
  };

  // Handler for when voice transcription is complete
  const handleTranscriptionDone = (transcription: string) => {
    // Append transcription to existing answer (or replace if empty)
    setQuestionAnswer((prev) => {
      if (prev.trim() === '') {
        return transcription;
      }
      return prev + ' ' + transcription;
    });
  };

  // Swipe handlers for iOS-style swipe-to-reveal
  const SWIPE_THRESHOLD = 80; // How far to swipe to reveal action

  const handleSwipeStart = (
    e: React.TouchEvent | React.MouseEvent,
    itemId: string,
  ) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    swipeStartX.current = clientX;
    // If a different item was swiped, close it
    if (swipedItemId && swipedItemId !== itemId) {
      setSwipedItemId(null);
      setSwipeOffset(0);
    }
  };

  const handleSwipeMove = (
    e: React.TouchEvent | React.MouseEvent,
    itemId: string,
  ) => {
    if (swipeStartX.current === null) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const diff = swipeStartX.current - clientX;

    // Only allow left swipe (positive diff)
    if (diff > 0) {
      setSwipedItemId(itemId);
      setSwipeOffset(Math.min(diff, SWIPE_THRESHOLD));
    } else if (swipedItemId === itemId) {
      // Swiping right to close
      setSwipeOffset(Math.max(0, SWIPE_THRESHOLD + diff));
    }
  };

  const handleSwipeEnd = (itemId: string) => {
    swipeStartX.current = null;

    // Snap to open or closed
    if (swipeOffset > SWIPE_THRESHOLD / 2) {
      setSwipedItemId(itemId);
      setSwipeOffset(SWIPE_THRESHOLD);
    } else {
      setSwipedItemId(null);
      setSwipeOffset(0);
    }
  };

  const handleSwipeIgnore = async (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    setSwipedItemId(null);
    setSwipeOffset(0);
    await handleIgnoreAction(itemId);
  };

  // Render functions for different item types
  // TODO: These will be reimplemented later
  const renderQuestion = (_item: DailyListItem) => {
    return <div />;
  };

  const renderStrategy = (_item: DailyListItem) => {
    return <div />;
  };

  const renderOpportunity = (_item: DailyListItem) => {
    return <div />;
  };

  const renderConfirmation = (_item: DailyListItem) => {
    return <div />;
  };

  const renderCall = (item: DailyListItem) => {
    const isCallActive = activeCallItemId === item.id;

    return (
      <div className='standard-gap-div-wrapper'>
        <div className='sizing-wrapper'>
          <GenericDailyCard
            onIgnore={() => handleIgnoreAction(item.id)}
            isIgnoring={processingAction === 'ignore'}
            onSnooze={(option) => handleSnoozeAction(item.id, option)}
            isSnoozing={processingAction === 'snooze'}
          >
            <div className='standard-daily-request should-scroll'>
              <ReactMarkdown>{item.callAgenda || ''}</ReactMarkdown>
            </div>
          </GenericDailyCard>
        </div>
        {isCallActive ? (
          <div className='wider-call-wrapper'>
            <p>Talk naturally, there are no wrong answers</p>
            <div className='webrtc-call-wrapper'>
              <WebRTCVoiceChat
                agentId={item.agentId}
                threadId={item.callThreadId || undefined}
                miniMode={true}
                autoStart={true}
                shouldHangup={shouldHangupCall}
                onConnectionChange={(connected) => {
                  if (!connected) {
                    // Call ended
                  }
                }}
                onHangupComplete={async () => {
                  setShouldHangupCall(false);
                  setActiveCallItemId(null);

                  // Optimistically mark as complete
                  setDailyListItems((prev) =>
                    prev.map((i) =>
                      i.id === item.id ? { ...i, status: 'complete' } : i,
                    ),
                  );

                  // Call API and revert on error
                  try {
                    await handleConfirmAction(item.id);
                  } catch (error) {
                    // Revert on error
                    setDailyListItems((prev) =>
                      prev.map((i) =>
                        i.id === item.id ? { ...i, status: 'selected' } : i,
                      ),
                    );
                  }
                }}
              />
            </div>
            <button
              className='finish-call-button'
              onClick={() => setShouldHangupCall(true)}
            >
              <PhoneHangupIcon />
              Finish Call
            </button>
          </div>
        ) : (
          <>
            {item.status === 'complete' && (
              <div className='call-complete-message'>
                <p>
                  Great! {item.agentNickname || item.agentName} will process the
                  call and go from there
                </p>
              </div>
            )}
            {voiceCallingEnabled && (
              <button
                className='standard-button call-agent-button'
                onClick={() => setActiveCallItemId(item.id)}
              >
                <PhoneIcon className='phone-icon' />
                {item.status === 'complete'
                  ? 'Keep chatting'
                  : `Call ${item.agentNickname || item.agentName} to discuss`}
              </button>
            )}
          </>
        )}
      </div>
    );
  };

  const renderItem = (item: DailyListItem) => {
    switch (item.itemType) {
      case 'question':
        return renderQuestion(item);
      case 'strategy':
        return renderStrategy(item);
      case 'opportunity':
        return renderOpportunity(item);
      case 'confirmation':
        return renderConfirmation(item);
      case 'call':
        return renderCall(item);
      default:
        return <div className='markdown-styled'>{item.whyImportantToday}</div>;
    }
  };

  // Completion screen
  if (screenState === 'completion') {
    return (
      <div className='dream-daily-container'>
        <div className='dream-daily-list'>
          <div className='date-header'>It's {formatDate()}</div>

          <div className='progress-wrapper'>
            {visibleDailyListItems.map((item, index) => (
              <div key={item.id} className={`progress-item complete`} />
            ))}
            <DreamDailyCompleteIcon />
          </div>
          <div className='daily-list-content'>
            <div className='completion-arrived'>
              <img src='https://d12b9vnf4u4q7j.cloudfront.net/generated-images/meme-1761590690557-965b475f-121f-4581-ad98-f06aba6e0fa6.png' />
              <h1>Congratulations!</h1>
              <p>
                You've completed your Dream Daily and earned 100 Dream points
                you fierce streaking dragon
              </p>
              <div className='points-display'>
                <span className='points-earned'>+100</span>
                <span className='total-points'>
                  {(streak || 1) * 100} total points
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Updates view
  if (currentView === 'updates') {
    return (
      <div className='dream-daily-container'>
        <div className='dream-daily-updates'>
          <div className='wrapped-for-mobile'>
            <div className='breadcrumb' onClick={navigateToList}>
              <div className='crumb-arrow-wrapper'>
                <ArrowLeftIcon className='back-arrow' />
              </div>
              <span>Back to Daily List</span>
            </div>
            <div className='updates-content'>
              <h1>Team Updates</h1>
              <p>Coming soon...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Item detail view
  if (currentView === 'item' && selectedItem) {
    return (
      <div className='dream-daily-container'>
        <div className='dream-daily-item'>
          <div className='wrapped-for-mobile'>
            <div className='breadcrumb' onClick={navigateToList}>
              <div className='crumb-arrow-wrapper'>
                <ArrowLeftIcon className='back-arrow' />
              </div>
              <span>Back to Daily List</span>
            </div>

            <div className='daily-list-content'>
              <div className='content-wrapper'>{renderItem(selectedItem)}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main list view
  const displayAgents = agents.slice(0, 3);

  return (
    <div className='dream-daily-container'>
      <div className='dream-daily-splash main-list'>
        <div className='dream-daily-main-list'>
          <div className='header'>
            <small>{formatDateLong()}</small>
            <DailyLogo />
          </div>

          <div className='big-fake-button' onClick={navigateToUpdates}>
            <div className='agent-avatars'>
              {displayAgents.map((agent) => (
                <img
                  key={agent.id}
                  src={agent.image_url || ''}
                  alt={agent.name}
                  className='agent-avatar'
                />
              ))}
            </div>
            <span>Show your team updates</span>
            <RightCaretIcon />
          </div>

          <div className='daily-list'>
            <h2>
              Daily List <span>{visibleDailyListItems.length}</span>
            </h2>
            <div className='items-wrapper'>
              {visibleDailyListItems.map((item) => {
                const isThisItemSwiped = swipedItemId === item.id;
                const currentOffset = isThisItemSwiped ? swipeOffset : 0;

                return (
                  <div
                    key={item.id}
                    className={`item swipeable ${isThisItemSwiped ? 'swiped' : ''}`}
                  >
                    <div
                      className='item-inner'
                      style={{
                        transform: `translateX(-${currentOffset}px)`,
                        transition:
                          swipeStartX.current === null
                            ? 'transform 0.2s ease-out'
                            : 'none',
                      }}
                      onTouchStart={(e) => handleSwipeStart(e, item.id)}
                      onTouchMove={(e) => handleSwipeMove(e, item.id)}
                      onTouchEnd={() => handleSwipeEnd(item.id)}
                      onMouseDown={(e) => handleSwipeStart(e, item.id)}
                      onMouseMove={(e) => handleSwipeMove(e, item.id)}
                      onMouseUp={() => handleSwipeEnd(item.id)}
                      onMouseLeave={() => handleSwipeEnd(item.id)}
                      onClick={() => {
                        if (currentOffset < 5) {
                          navigateToItem(item.id);
                        }
                      }}
                    >
                      <div className='left-icon'>
                        {item.itemType === 'opportunity' ? (
                          <img
                            src={fireIcon}
                            alt='Opportunity'
                            className='fire-icon'
                          />
                        ) : (
                          <div className='dreamie-wrapper'>
                            <img
                              src={
                                agents.find((a) => a.id === item.agentId)
                                  ?.image_url ?? ''
                              }
                              alt={item.agentName ?? 'Agent'}
                              className='agent-avatar'
                            />
                          </div>
                        )}
                      </div>
                      <div className='item-center'>
                        <h3>{item.agentNickname || item.agentName}</h3>
                        <p className='item-summary'>
                          <strong>{item.summary}</strong>
                          {item.subSummary && ` ${item.subSummary}`}
                        </p>
                      </div>
                    </div>
                    <div
                      className='swipe-action ignore-action'
                      onClick={(e) => handleSwipeIgnore(e, item.id)}
                      style={{
                        opacity: currentOffset / 80,
                      }}
                    >
                      <IgnoreCircleIcon />
                      <span>Ignore</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DreamDaily;
